import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';

const C = {
  rose: '#C97B77', rosePale: '#F9EDEC', roseLt: '#E8B4B1',
  slate: '#6B7FA0', slatePale: '#EEF2F8',
  ink: '#2C2C35', inkMid: '#555567', inkLt: '#888899',
  border: '#E8E4DF', borderLt: '#F0EDE8',
  white: '#FFFFFF', offWhite: '#FAFAF8',
  sage: '#7A9E8E', sagePale: '#EBF4F0',
  red: '#DC2626', redPale: '#FEF2F2',
};

export default function GroupsPage() {
  const [groups, setGroups]               = useState([]);
  const [allBooks, setAllBooks]           = useState([]);
  const [allUsers, setAllUsers]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupUsers, setGroupUsers]       = useState([]);
  const [rightTab, setRightTab]           = useState('info');
  const [toast, setToast]                 = useState(null);
  const [showModal, setShowModal]         = useState(false);
  const [editMode, setEditMode]           = useState(false);
  const [bookSearch, setBookSearch]       = useState('');
  const [userSearch, setUserSearch]       = useState('');
  const [form, setForm]                   = useState({ name: '', books: [], price: 0, showInPlans: false });

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 2500); };

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [gRes, bRes, uRes] = await Promise.all([
        adminAPI.getGroups(),
        adminAPI.getBooks({ limit: 500 }),
        adminAPI.getUsers({ limit: 500 }),
      ]);
      setGroups(gRes.data || []);
      setAllBooks(bRes.data || []);
      setAllUsers(uRes.data || []);
    } catch { showToast('Failed to load data', 'error'); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setForm({ name: '', books: [], price: 0, showInPlans: false });
    setEditMode(false);
    setShowModal(true);
    setBookSearch('');
  };

  const openEdit = (group) => {
    setForm({ name: group.name, books: group.books || [], price: group.price || 0, showInPlans: group.showInPlans || false });
    setEditMode(true);
    setShowModal(true);
    setBookSearch('');
  };

  const handleSave = async () => {
    if (!form.name.trim()) return showToast('Group name is required', 'error');
    try {
      const payload = { name: form.name, books: form.books, price: parseFloat(form.price) || 0, showInPlans: form.showInPlans };
      if (editMode) {
        await adminAPI.updateGroup(selectedGroup._id, payload);
        showToast('Group updated ✓');
      } else {
        await adminAPI.createGroup(payload);
        showToast('Group created ✓');
      }
      setShowModal(false);
      await loadAll();
      setSelectedGroup(null);
    } catch { showToast('Failed to save', 'error'); }
  };

  const handleDelete = async (group) => {
    if (!window.confirm(`Delete "${group.name}"?`)) return;
    try {
      await adminAPI.deleteGroup(group._id);
      showToast('Group deleted');
      if (selectedGroup?._id === group._id) setSelectedGroup(null);
      loadAll();
    } catch { showToast('Failed to delete', 'error'); }
  };

  const selectGroup = async (group) => {
    setSelectedGroup(group);
    setRightTab('info');
    try {
      const res = await adminAPI.getGroupUsers(group._id);
      setGroupUsers(res.data || []);
    } catch { setGroupUsers([]); }
  };

  const handleAddUser = async (userId) => {
    try {
      await adminAPI.updateGroupUsers(selectedGroup._id, [userId], 'add');
      showToast('User added ✓');
      const res = await adminAPI.getGroupUsers(selectedGroup._id);
      setGroupUsers(res.data || []);
    } catch { showToast('Failed to add user', 'error'); }
  };

  const handleRemoveUser = async (userId) => {
    try {
      await adminAPI.updateGroupUsers(selectedGroup._id, [userId], 'remove');
      showToast('User removed');
      const res = await adminAPI.getGroupUsers(selectedGroup._id);
      setGroupUsers(res.data || []);
    } catch { showToast('Failed to remove user', 'error'); }
  };

  const toggleBook = (bookId) => {
    setForm(f => ({
      ...f,
      books: f.books.includes(bookId) ? f.books.filter(b => b !== bookId) : [...f.books, bookId]
    }));
  };

  const groupUserIds   = groupUsers.map(u => u.userId || u.auth0Id);
  const nonGroupUsers  = allUsers.filter(u => {
    const id = u.userId || u.auth0Id;
    if (groupUserIds.includes(id)) return false;
    if (!userSearch) return true;
    return (u.name || '').toLowerCase().includes(userSearch.toLowerCase()) ||
           (u.email || '').toLowerCase().includes(userSearch.toLowerCase());
  });
  const filteredBooks  = allBooks.filter(b => {
    const name = b.book_name || b.book_title || '';
    return !bookSearch || name.toLowerCase().includes(bookSearch.toLowerCase());
  });
  const selectedGroupData = groups.find(g => g._id === selectedGroup?._id) || selectedGroup;

  const groupLabel = (group) => {
    if (!group.showInPlans) return null;
    if (!group.price || group.price === 0) return { text: 'FREE', bg: C.sagePale, color: C.sage };
    return { text: `₹${group.price}`, bg: '#FEF3C7', color: '#92400E' };
  };

  return (
    <div style={{ display: 'flex', height: '100%', fontFamily: 'Nunito,sans-serif', background: C.offWhite }}>

      {/* ── LEFT PANEL ── */}
      <div style={{ width: 300, flexShrink: 0, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', background: C.white }}>
        <div style={{ padding: '20px 16px 14px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.inkLt, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>Access Control</div>
          <div style={{ fontFamily: 'Playfair Display,serif', fontSize: 20, fontWeight: 700, color: C.ink, marginBottom: 14 }}>
            Group <em style={{ color: C.rose }}>Management</em>
          </div>
          <button onClick={openCreate}
            style={{ width: '100%', padding: '10px', background: C.rose, color: 'white', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <span style={{ fontSize: 18, lineHeight: 1 }}>+</span> Create New Group
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: C.inkLt, fontSize: 12 }}>Loading…</div>
          ) : groups.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🗂️</div>
              <div style={{ fontSize: 13, color: C.inkLt }}>No groups yet</div>
            </div>
          ) : groups.map(group => {
            const label = groupLabel(group);
            return (
              <div key={group._id} onClick={() => selectGroup(group)}
                style={{ padding: '14px 16px', cursor: 'pointer', borderBottom: `1px solid ${C.borderLt}`, background: selectedGroup?._id === group._id ? C.rosePale : 'transparent', borderLeft: selectedGroup?._id === group._id ? `3px solid ${C.rose}` : '3px solid transparent', transition: 'all 0.15s' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: selectedGroup?._id === group._id ? C.rose : C.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {group.name}
                      </div>
                      {label && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: label.bg, color: label.color, flexShrink: 0 }}>{label.text}</span>}
                    </div>
                    <div style={{ fontSize: 11, color: C.inkLt }}>
                      {(group.books || []).length} books · {group.showInPlans ? 'Visible in Plans' : 'Admin only'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0, marginLeft: 8 }}>
                    <button onClick={e => { e.stopPropagation(); selectGroup(group); openEdit(group); }}
                      style={{ padding: '4px 8px', background: C.slatePale, color: C.slate, border: 'none', borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>Edit</button>
                    <button onClick={e => { e.stopPropagation(); handleDelete(group); }}
                      style={{ padding: '4px 8px', background: C.redPale, color: C.red, border: 'none', borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>✕</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!selectedGroup ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: C.inkLt }}>
            <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }}>🗂️</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.ink, marginBottom: 6 }}>Select a group</div>
            <div style={{ fontSize: 12 }}>Click any group on the left to manage it</div>
          </div>
        ) : (
          <>
            <div style={{ padding: '20px 24px 0', background: C.white, borderBottom: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontFamily: 'Playfair Display,serif', fontSize: 20, fontWeight: 700, color: C.ink }}>{selectedGroupData?.name}</div>
                    {selectedGroupData?.showInPlans && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
                        background: !selectedGroupData?.price || selectedGroupData?.price === 0 ? C.sagePale : '#FEF3C7',
                        color: !selectedGroupData?.price || selectedGroupData?.price === 0 ? C.sage : '#92400E' }}>
                        {!selectedGroupData?.price || selectedGroupData?.price === 0 ? 'FREE PLAN' : `₹${selectedGroupData?.price} PLAN`}
                      </span>
                    )}
                    {!selectedGroupData?.showInPlans && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: C.slatePale, color: C.slate }}>ADMIN ONLY</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: C.inkLt, marginTop: 2 }}>
                    {(selectedGroupData?.books || []).length} books assigned
                  </div>
                </div>
                <button onClick={() => openEdit(selectedGroupData)}
                  style={{ padding: '8px 16px', background: C.rose, color: 'white', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  Edit Group
                </button>
              </div>
              <div style={{ display: 'flex', gap: 0 }}>
                {[
                  { key: 'info', label: 'Overview' },
                  { key: 'users', label: `Members (${groupUsers.length})` },
                  { key: 'addUser', label: '+ Add Users' },
                ].map(t => (
                  <button key={t.key} onClick={() => setRightTab(t.key)}
                    style={{ padding: '10px 20px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, background: 'transparent', fontFamily: 'Nunito,sans-serif', color: rightTab === t.key ? C.rose : C.inkLt, borderBottom: rightTab === t.key ? `2px solid ${C.rose}` : '2px solid transparent' }}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
              {rightTab === 'info' && (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
                    {[
                      { label: 'Books Assigned', value: (selectedGroupData?.books || []).length, icon: '📚' },
                      { label: 'Members', value: groupUsers.length, icon: '👥' },
                      { label: 'Visible in Plans', value: selectedGroupData?.showInPlans ? 'Yes' : 'No', icon: '👁️' },
                    ].map(stat => (
                      <div key={stat.label} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px 20px' }}>
                        <div style={{ fontSize: 24, marginBottom: 8 }}>{stat.icon}</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: C.ink, fontFamily: 'Playfair Display,serif' }}>{stat.value}</div>
                        <div style={{ fontSize: 11, color: C.inkLt, marginTop: 2 }}>{stat.label}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>Assigned Books</div>
                    {(selectedGroupData?.books || []).length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 24, color: C.inkLt, fontSize: 12 }}>No books — click Edit Group to add</div>
                    ) : (selectedGroupData?.books || []).map(bookId => {
                      const book = allBooks.find(b => b._id === bookId);
                      return (
                        <div key={bookId} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: `1px solid ${C.borderLt}` }}>
                          <div style={{ width: 32, height: 32, borderRadius: 6, background: C.rosePale, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>📖</div>
                          <div style={{ flex: 1, fontSize: 13, color: C.ink, fontWeight: 600 }}>{book?.book_name || book?.book_title || `Book #${bookId}`}</div>
                          {book && book.enabled === false && <span style={{ fontSize: 9, color: C.red, fontWeight: 700, background: C.redPale, padding: '2px 6px', borderRadius: 4 }}>DISABLED</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {rightTab === 'users' && (
                <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
                  {groupUsers.length === 0 ? (
                    <div style={{ padding: 48, textAlign: 'center', color: C.inkLt }}>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>👥</div>
                      <div style={{ fontSize: 13 }}>No members yet</div>
                    </div>
                  ) : groupUsers.map((u, i) => (
                    <div key={u._id || i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: `1px solid ${C.borderLt}` }}>
                      <div style={{ width: 36, height: 36, borderRadius: '50%', background: C.rosePale, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: C.rose, flexShrink: 0 }}>
                        {(u.name || 'U')[0].toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>{u.name || 'No name'}</div>
                        <div style={{ fontSize: 11, color: C.inkLt }}>{u.email}</div>
                      </div>
                      <button onClick={() => handleRemoveUser(u.userId || u.auth0Id)}
                        style={{ padding: '6px 12px', background: C.redPale, color: C.red, border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {rightTab === 'addUser' && (
                <div>
                  <input value={userSearch} onChange={e => setUserSearch(e.target.value)}
                    placeholder="Search users…"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: 'Nunito,sans-serif', color: C.ink, outline: 'none', boxSizing: 'border-box', marginBottom: 16 }} />
                  <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
                    {nonGroupUsers.length === 0 ? (
                      <div style={{ padding: 32, textAlign: 'center', color: C.inkLt, fontSize: 12 }}>All users are in this group</div>
                    ) : nonGroupUsers.map((u, i) => (
                      <div key={u._id || i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: `1px solid ${C.borderLt}` }}>
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: C.slatePale, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: C.slate, flexShrink: 0 }}>
                          {(u.name || 'U')[0].toUpperCase()}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>{u.name || 'No name'}</div>
                          <div style={{ fontSize: 11, color: C.inkLt }}>{u.email}</div>
                        </div>
                        <button onClick={() => handleAddUser(u.userId || u.auth0Id)}
                          style={{ padding: '6px 14px', background: C.rose, color: 'white', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                          + Add
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(44,44,53,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div style={{ background: C.white, borderRadius: 16, width: 560, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.2)', overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontFamily: 'Playfair Display,serif', fontSize: 18, fontWeight: 700, color: C.ink }}>
                {editMode ? 'Edit Group' : 'Create New Group'}
              </div>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: C.inkLt }}>✕</button>
            </div>

            <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
              {/* Name */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.inkLt, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Group Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Law Students Batch 2025"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 13, fontFamily: 'Nunito,sans-serif', color: C.ink, outline: 'none', boxSizing: 'border-box' }} />
              </div>

              {/* Show in Plans toggle */}
              <div style={{ marginBottom: 16, padding: '14px 16px', background: C.offWhite, borderRadius: 10, border: `1px solid ${C.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>Show in Plans Page</div>
                    <div style={{ fontSize: 11, color: C.inkLt, marginTop: 2 }}>Users will see this group on the Plans page</div>
                  </div>
                  <button onClick={() => setForm(f => ({ ...f, showInPlans: !f.showInPlans }))}
                    style={{ width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', background: form.showInPlans ? C.rose : C.border, position: 'relative', transition: 'background 0.2s' }}>
                    <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'white', position: 'absolute', top: 3, left: form.showInPlans ? 23 : 3, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
                  </button>
                </div>

                {/* Price — only show when showInPlans is true */}
                {form.showInPlans && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.inkLt, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>
                      Price (₹) — 0 for Free
                    </label>
                    <input type="number" min="0" value={form.price}
                      onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                      placeholder="0"
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 13, fontFamily: 'Nunito,sans-serif', color: C.ink, outline: 'none', boxSizing: 'border-box' }} />
                    <div style={{ fontSize: 11, color: C.inkLt, marginTop: 6 }}>
                      {!form.price || parseFloat(form.price) === 0
                        ? '✅ Free — Users can join with one click'
                        : `💰 Paid — Users will see ₹${form.price} on Plans page`}
                    </div>
                  </div>
                )}
              </div>

              {/* Books */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Select Books <span style={{ color: C.rose }}>({form.books.length} selected)</span>
                  </label>
                  {form.books.length > 0 && (
                    <button onClick={() => setForm(f => ({ ...f, books: [] }))}
                      style={{ fontSize: 10, color: C.red, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>Clear all</button>
                  )}
                </div>
                <input value={bookSearch} onChange={e => setBookSearch(e.target.value)}
                  placeholder="Search books…"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12, fontFamily: 'Nunito,sans-serif', color: C.ink, outline: 'none', boxSizing: 'border-box', marginBottom: 8 }} />
                <div style={{ maxHeight: 240, overflowY: 'auto', border: `1px solid ${C.border}`, borderRadius: 8 }}>
                  {filteredBooks.map(book => {
                    const name = book.book_name || book.book_title || `Book #${book._id}`;
                    const selected = form.books.includes(book._id);
                    return (
                      <label key={book._id}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer', borderBottom: `1px solid ${C.borderLt}`, background: selected ? C.rosePale : 'transparent' }}>
                        <input type="checkbox" checked={selected} onChange={() => toggleBook(book._id)}
                          style={{ accentColor: C.rose, width: 15, height: 15, flexShrink: 0 }} />
                        <span style={{ fontSize: 12, color: C.ink, flex: 1 }}>{name}</span>
                        {book.enabled === false && <span style={{ fontSize: 9, color: C.red, fontWeight: 700, background: C.redPale, padding: '1px 5px', borderRadius: 3 }}>DISABLED</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowModal(false)}
                style={{ padding: '10px 20px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', color: C.inkMid, fontFamily: 'Nunito,sans-serif' }}>
                Cancel
              </button>
              <button onClick={handleSave}
                style={{ padding: '10px 24px', background: C.rose, color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'Nunito,sans-serif' }}>
                {editMode ? 'Save Changes' : 'Create Group'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9999,
          background: toast.type === 'error' ? C.red : C.sage, color: 'white',
          padding: '10px 20px', borderRadius: 12, fontSize: 12, fontWeight: 700,
          fontFamily: 'Nunito,sans-serif', boxShadow: '0 4px 20px rgba(0,0,0,.2)', whiteSpace: 'nowrap' }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}